import http from '@/plugins/http';
import mixpanel from '@/plugins/mixpanel';
import * as Sentry from '@sentry/vue';
import moment from 'moment';
import { useAccountStore } from '@/stores/account';
import { useAuthStore } from '@/stores/auth';
import { useNotifierStore } from '@/stores/notifier';
import { useVideoPopupStore } from '@/stores/videoPopup';
import reportError from '@/utils/reportError';
import { useCompanyStore } from '@/stores/company';
import permissionService from '../permission';
import accountTransformer from '../../transformers/account';

const checkPricingEvolution = (account, permissions) => {
  const { company } = account;
  if (!permissions || !permissions.customerBillingInvoices) {
    return;
  }

  if (company.hourly_rate_evolution !== null && company.hourly_rate_evolution.new_hourly_rate > 0 && company.hourly_rate_evolution.display_warning) {
    const date = moment(company.hourly_rate_evolution.date);
    const newHourlyRate = company.hourly_rate_evolution.new_hourly_rate;
    const oldHourlyRate = company.hourly_rate;
    const increaseAmount = Math.round((newHourlyRate - oldHourlyRate) * 10, 1) / 10;
    const increaseRate = Number(((newHourlyRate - oldHourlyRate) / oldHourlyRate) * 100).toFixed(1);
    useNotifierStore().open({
      message: increaseRate <= 1.5 ? 'home.hourly_rate_evolve.banner' : 'home.hourly_rate_evolve_hide_rate.banner',
      type: 'aqua',
      name: 'hourly_rate',
      params: {
        date: date.format('D MMMM YYYY'),
        increaseAmount: increaseAmount.toString().replace('.', ','),
        increaseRate,
        newHourlyRate,
      },
      close: true,
      close_name: 'hide_banner_evolve',
    });
  }
};

const checkIfNewUserVideoPopup = (link) => {
  if (link && link.length) {
    useVideoPopupStore().open({
      link,
      videoName: 'first_connection',
      params: { arcadePadding: 'calc(44.375% + 41px)' },
      close_name: 'first_connection',
    });
  }
};

// SP-15066 — an impersonating admin does not know the customer's credentials: the
// other interface opens on the same session (same API, same token).
const impersonationUrl = (url) => {
  const { fromAdminId } = useAuthStore();

  return fromAdminId ? `${url}/#/auth/${localStorage.getItem('id_token')}?from_admin_id=${fromAdminId}` : url;
};

// When the request succeeds
const success = (account, onFailureCallback, onSuccessCallback) => {
  if (!account.customer) {
    window.console.error('Not Logged as customer');
    if (onFailureCallback) {
      onFailureCallback('not_customer');
    }
    useAuthStore().logout();
  } else {
    // set cookies for site vitrine
    document.cookie = `alreadyAuth=true;domain=.studentpop.fr;expires=${new Date(new Date().setDate(new Date().getDate() + 30))};samesite=Lax;`;

    // set mixpanel identify user id
    mixpanel.people.set({
      $email: account.email,
      $first_name: account.firstName,
      $last_name: account.lastName,
      USER_ID: account.id,
    });
    mixpanel.identify(account.id);

    if (account.tracking) {
      mixpanel.register(account.tracking);
    }

    mixpanel.register({
      FirstName: account.firstName,
      LastName: account.lastName,
      Email: account.email,
    });

    useAccountStore().store(account);
    // test domain for trusted or not
    const domain = account.email.split('@')[1];
    if (domain !== 'techsell.fr') {
      useAuthStore().trusted();
    }
    Sentry.setUser({
      email: account.email,
      id: account.id,
    });

    if (window.ineum) {
      const name = `${account.firstName} ${account.lastName}`;
      window.ineum('user', account.id, name, account.email);
    }

    window.Intercom('update', {
      email: account.email,
      user_id: account.id,
      name: `${account.firstName} ${account.lastName}`,
    });

    permissionService.all().then((permissions) => {
      checkPricingEvolution(account, permissions);
      checkIfNewUserVideoPopup(account.arcadeLinks.url_arcade_first_user_connection);
    });

    if (account.talentWorkBanner) {
      useNotifierStore().open({
        message: 'header.talent_work_banner.text',
        type: 'aqua',
        params: { url: impersonationUrl(account.brandUrls.talent_work) },
        name: 'talent_work',
        close: true,
      });
    }

    if (account.paymentStatus === 'blocked') {
      const message = 'home.payment_blocked.headband_text';
      useNotifierStore().open({
        message,
        type: 'danger',
        params: { link: '/invoice' },
        name: 'payment_blocked',
      });
    } else if (account.paymentStatus === 'late') {
      const message = 'home.payment_late.headband_text';
      useNotifierStore().open({
        message,
        type: 'warning',
        params: { link: '/invoice' },
        name: 'payment_late',
      });
    } else if (account.orderBlocked) {
      const message = 'home.blocked.headband_text';
      useNotifierStore().open({ message, type: 'danger', name: 'blocked' });
    }

    if (onSuccessCallback) {
      onSuccessCallback(account);
    }
  }
};

// When the request fails
const failed = (error, onFailureCallback) => {
  // Some callers (app.vue, updatePassword.js) pass no callback.
  if (onFailureCallback) {
    onFailureCallback(error);
  }
};

function find(onFailureCallback, onSuccessCallback) {
  const token = localStorage.getItem('id_token');
  http.get('/me')
    .then((response) => {
      // An impersonation logs in while `app.vue` still fetches /me with the previous token.
      if (localStorage.getItem('id_token') !== token) {
        return;
      }
      // Handle the customers for StudentPop or TalentWork
      const account = accountTransformer.fetch(response.data);
      if (account.brandRedirectUrl) {
        window.location.assign(account.brandRedirectUrl);
      } else if (account.company && account.companies.length
        && !account.companies.some((company) => company.id === account.company.id)) {
        // SP-15066 — the active company is of the other brand
        useCompanyStore().rememberAccount(account.companies[0].id);
        find(onFailureCallback, onSuccessCallback);
      } else {
        success(account, onFailureCallback, onSuccessCallback);
      }
    })
    .catch((error) => {
      reportError(error, 'error finding current user account');
      failed(error, onFailureCallback);
    });
}

export default find;
